import { Signal, effect, inject, signal, untracked } from '@angular/core';
import { Store } from '@ngrx/store';

import { ReferenceNamesService } from '@core/services/reference-names/reference-names.service';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { isNil } from '@core/utils/shared-utils';

export type ResourceKind = 'vehicle' | 'driver' | 'trailer' | 'company';

/** A named registration of a vehicle, trailer or driver, resolved by its code. */
export type RegistrationNameKind =
  | 'vehicle_registration'
  | 'trailer_registration'
  | 'driver_registration';

type NameKind = ResourceKind | RegistrationNameKind;

const ENTITY_TYPE: Record<NameKind, string> = {
  vehicle: 'VEHICLE',
  driver: 'DRIVER',
  trailer: 'TRAILER',
  company: 'COMPANY',
  vehicle_registration: 'VEHICLE_REGISTRATION',
  trailer_registration: 'TRAILER_REGISTRATION',
  driver_registration: 'DRIVER_REGISTRATION'
};

/**
 * Resolves the names of the given resources. The returned function gives a resource's name once
 * known and its uid until then, or when the backend has no name for it. Needs an injection context.
 */
export function injectResourceNames(
  resources: Signal<{ kind: NameKind; uid: string | null }[]>
): (kind: NameKind, uid: string | null) => string {
  const store = inject(Store);
  const referenceNames = inject(ReferenceNamesService);
  const dataspaceState = store.selectSignal(selectDataspaceState);
  const plan = store.selectSignal(selectPlan);
  const names = signal<ReadonlyMap<string, string>>(new Map());

  effect(() => {
    const dataSpaceId = dataspaceState()?.dataspace?.id;
    const planId = plan()?.id;
    const wanted = resources();
    if (isNil(dataSpaceId)) return;
    untracked(() => {
      for (const kind of Object.keys(ENTITY_TYPE) as NameKind[]) {
        const uids = wanted.flatMap((r) => (r.kind === kind && r.uid ? [r.uid] : []));
        if (uids.length === 0) continue;
        referenceNames
          .resolveNames$(ENTITY_TYPE[kind], uids, {
            dataSpaceId,
            ...(isNil(planId) ? {} : { planId })
          })
          .subscribe((resolved) =>
            names.update((known) => {
              const next = new Map(known);
              for (const [uid, name] of resolved) next.set(`${kind}:${uid}`, name);
              return next;
            })
          );
      }
    });
  });

  return (kind, uid) => (uid ? (names().get(`${kind}:${uid}`) ?? uid) : '');
}
